/**
 * useTasks Hook
 * Custom hook for managing tasks state and operations
 * FINLIST REAL-TIME: Uses Firestore subscriptions for instant updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getTodoLists,
  getTasksByList,
  getAllTasks,
  getFilteredTasks,
  createTask,
  updateTask,
  moveTask,
  deleteTask,
  toggleTaskStatus,
  archiveTask,
  unarchiveTask,
  createTodoList,
  updateTodoList,
  deleteTodoList,
  subscribeToTasksByList,
  subscribeToAllTasks
} from '@/services/finTasksService';

export function useTasks(listId = null, filter = 'all') {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitialLoad = useRef(true);

  // Set up real-time subscription for tasks
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      setTasks([]);
      return;
    }

    setLoading(true);
    setError(null);
    isInitialLoad.current = true;

    let unsubscribe;

    if (listId) {
      // Subscribe to specific list's tasks
      unsubscribe = subscribeToTasksByList(
        user.uid,
        listId,
        (fetchedTasks) => {
          setTasks(fetchedTasks);
          if (isInitialLoad.current) {
            setLoading(false);
            isInitialLoad.current = false;
          }
        }
      );
    } else {
      // Subscribe to all tasks across all lists
      // Filter logic applied in subscription callback
      unsubscribe = subscribeToAllTasks(
        user.uid,
        (allTasks) => {
          // Apply filter logic based on filter prop
          let filteredTasks = allTasks;
          
          if (filter === 'today') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            filteredTasks = allTasks.filter(task => {
              if (!task.dueDate) return false;
              const dueDate = new Date(task.dueDate);
              return dueDate >= today && dueDate < tomorrow;
            });
          } else if (filter === 'overdue') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            filteredTasks = allTasks.filter(task => {
              if (!task.dueDate) return false;
              const dueDate = new Date(task.dueDate);
              return dueDate < today && task.status !== 'done';
            });
          } else if (filter === 'upcoming') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const nextWeek = new Date(today);
            nextWeek.setDate(nextWeek.getDate() + 7);
            filteredTasks = allTasks.filter(task => {
              if (!task.dueDate) return false;
              const dueDate = new Date(task.dueDate);
              return dueDate >= today && dueDate <= nextWeek;
            });
          } else if (filter === 'archived') {
            filteredTasks = allTasks.filter(task => task.status === 'archived');
          }
          // 'all' filter uses allTasks as-is (already excludes archived)
          
          setTasks(filteredTasks);
          if (isInitialLoad.current) {
            setLoading(false);
            isInitialLoad.current = false;
          }
        },
        { excludeArchived: filter !== 'archived' }
      );
    }

    // Cleanup subscription on unmount or when dependencies change
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.uid, listId, filter]);

  // Fallback fetch function for manual refresh
  const fetchTasks = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let fetchedTasks;

      if (listId) {
        fetchedTasks = await getTasksByList(user.uid, listId);
      } else {
        fetchedTasks = await getFilteredTasks(user.uid, filter);
      }

      setTasks(fetchedTasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, listId, filter]);

  const addTask = useCallback(async (taskData) => {
    if (!user?.uid || !taskData.listId) {
      throw new Error('User and list ID required');
    }

    try {
      const newTask = await createTask(user.uid, taskData.listId, taskData);
      setTasks(prev => [newTask, ...prev]);
      return newTask;
    } catch (err) {
      console.error('Error adding task:', err);
      throw err;
    }
  }, [user?.uid]);

  const editTask = useCallback(async (taskListId, taskId, updates) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      // Find the current task to detect list moves
      const current = tasks.find(t => t.id === taskId);

      if (current && current.listId && current.listId !== taskListId) {
        // Move task between lists when listId changes
        await moveTask(user.uid, current.listId, taskListId, taskId, updates);
      } else {
        // Regular update in the same list
        await updateTask(user.uid, taskListId, taskId, updates);
      }

      setTasks(prev => prev.map(task => (
        task.id === taskId ? { ...task, ...updates, listId: taskListId } : task
      )));
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  }, [user?.uid, tasks]);

  const removeTask = useCallback(async (taskListId, taskId) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      await deleteTask(user.uid, taskListId, taskId);
      setTasks(prev => prev.filter(task => task.id !== taskId));
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  }, [user?.uid]);

  const toggleTask = useCallback(async (taskListId, taskId) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      const newStatus = await toggleTaskStatus(user.uid, taskListId, taskId);
      setTasks(prev =>
        prev.map(task =>
          task.id === taskId ? { ...task, status: newStatus } : task
        )
      );
    } catch (err) {
      console.error('Error toggling task:', err);
      throw err;
    }
  }, [user?.uid]);

  const archiveTaskItem = useCallback(async (taskListId, taskId) => {
    console.log('[useTasks.archiveTaskItem] Called with:', { taskListId, taskId, userId: user?.uid });
    
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      await archiveTask(user.uid, taskListId, taskId);
      console.log('[useTasks.archiveTaskItem] archiveTask succeeded, updating local state');
      setTasks(prev =>
        prev.map(task =>
          task.id === taskId ? { ...task, status: 'archived', archivedAt: new Date().toISOString() } : task
        )
      );
    } catch (err) {
      console.error('Error archiving task:', err);
      throw err;
    }
  }, [user?.uid]);

  const unarchiveTaskItem = useCallback(async (taskListId, taskId) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      await unarchiveTask(user.uid, taskListId, taskId);
      // Refetch to get correct status after unarchive
      await fetchTasks();
    } catch (err) {
      console.error('Error unarchiving task:', err);
      throw err;
    }
  }, [user?.uid, fetchTasks]);

  // Reorder tasks (update local state and persist order to Firestore)
  const reorderTasks = useCallback(async (newOrderedTasks) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    // Update local state immediately for instant feedback
    setTasks(newOrderedTasks);

    try {
      // Persist the new order to Firestore
      // Update each task with its new order index
      const updates = newOrderedTasks.map((task, index) => 
        updateTask(user.uid, task.listId, task.id, { order: index })
      );
      
      await Promise.all(updates);
    } catch (err) {
      console.error('Error reordering tasks:', err);
      // Refetch to restore correct order on error
      await fetchTasks();
      throw err;
    }
  }, [user?.uid, fetchTasks]);

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks,
    addTask,
    editTask,
    removeTask,
    toggleTask,
    archiveTaskItem,
    unarchiveTaskItem,
    reorderTasks
  };
}

export default useTasks;
